import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Modal, Alert, Platform } from 'react-native';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { setDoc, doc } from 'firebase/firestore';
import { MaterialIcons } from '@expo/vector-icons';

const showAlert = (title, message) => {
  if (Platform.OS === 'web') {
    window.alert(`${title}: ${message}`);
  } else {
    Alert.alert(title, message);
  }
};

const RegisterCompany = ({ navigation }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [phone, setPhone] = useState('');
  const [cep, setCep] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  const handleRegister = async () => {
    if (!email || !password || !companyName || !cnpj || !phone || !cep || !street || !number) {
      showAlert('Erro', 'Por favor, preencha todos os campos.');
      return;
    }

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await setDoc(doc(db, 'usuarios', user.uid), {
        companyName,
        cnpj,
        phone,
        cep,
        street,
        number,
        tipo: 'empresa',
        ativo: false,
        email: email,
        password: password, 
      });

      setShowSuccessModal(true);

      setTimeout(() => {
        setShowSuccessModal(false);
        navigation.reset({
          index: 0,
          routes: [{ name: 'LoginScreen' }],
        });
      }, 1000);

    } catch (error) {
      let errorMessage = 'Erro ao registrar empresa.';

      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Este email já está em uso. Por favor, use outro email.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Email inválido. Por favor, insira um email válido.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'A senha é muito fraca. Use uma senha com no mínimo 6 caracteres.';
      }

      showAlert('Erro', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity style={styles.backButtonContainer} onPress={() => navigation.goBack()}>
        <MaterialIcons name="arrow-back" size={24} color="#FFF" />
      </TouchableOpacity>

      <Text style={styles.title}>Registrar Empresa</Text>

      <View style={styles.inputContainer}>
        <MaterialIcons name="email" size={24} color="#000" />
        <TextInput
          placeholder="Email"
          value={email}
          onChangeText={(text) => setEmail(text.replace(/\s/g, ''))} // Remove espaços enquanto o usuário digita
          style={styles.input}
          keyboardType="email-address"
          autoCapitalize="none"
        />
      </View>

      <View style={styles.inputContainer}>
        <MaterialIcons name="lock" size={24} color="#000" />
        <TextInput
          placeholder="Senha"
          value={password}
          onChangeText={(text) => setPassword(text.replace(/\s/g, ''))} // Remove espaços enquanto o usuário digita
          secureTextEntry
          style={styles.input}
        />
      </View>

      <View style={styles.inputContainer}>
        <MaterialIcons name="business" size={24} color="#000" />
        <TextInput
          placeholder="Nome da Empresa"
          value={companyName}
          onChangeText={setCompanyName}
          style={styles.input}
        />
      </View>

      <View style={styles.inputContainer}>
        <MaterialIcons name="credit-card" size={24} color="#000" />
        <TextInput
          placeholder="CNPJ"
          value={cnpj}
          onChangeText={setCnpj}
          style={styles.input}
          keyboardType="numeric"
          maxLength={14} // Limite de 14 dígitos
        />
      </View>

      <View style={styles.inputContainer}>
        <MaterialIcons name="phone" size={24} color="#000" />
        <TextInput
          placeholder="Telefone"
          value={phone}
          onChangeText={setPhone}
          style={styles.input}
          keyboardType="phone-pad"
        />
      </View>

      <View style={styles.inputContainer}>
        <MaterialIcons name="location-on" size={24} color="#000" />
        <TextInput
          placeholder="CEP"
          value={cep}
          onChangeText={setCep}
          style={styles.input}
          keyboardType="numeric"
          maxLength={8}
        />
      </View>

      <View style={styles.inputContainer}>
        <MaterialIcons name="location-city" size={24} color="#000" />
        <TextInput
          placeholder="Rua"
          value={street}
          onChangeText={setStreet}
          style={styles.input}
        />
      </View>

      <View style={styles.inputContainer}>
        <MaterialIcons name="home" size={24} color="#000" />
        <TextInput
          placeholder="Número"
          value={number}
          onChangeText={setNumber}
          style={styles.input}
          keyboardType="numeric"
        />
      </View>

      {loading ? (
        <TouchableOpacity style={[styles.registerButton, { backgroundColor: '#888' }]} disabled>
          <Text style={styles.registerButtonText}>Registrando...</Text>
        </TouchableOpacity>
      ) : (
        <TouchableOpacity style={styles.registerButton} onPress={handleRegister}>
          <Text style={styles.registerButtonText}>Registrar</Text>
        </TouchableOpacity>
      )}

      <Modal
        visible={showSuccessModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowSuccessModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <MaterialIcons name="check-circle" size={50} color="#4CAF50" />
            <Text style={styles.modalText}>Empresa registrada com sucesso!</Text>
          </View>
        </View>
      </Modal>
    </View>
  );
};
const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    padding: 16,
    backgroundColor: '#2c5364', // Fundo escuro
  },
  backButtonContainer: {
    position: 'absolute',
    top: 40,
    left: 16,
    padding: 10,
    backgroundColor: '#203a43', // Fundo escuro consistente
    borderRadius: 50,
    shadowColor: '#000',
    shadowOpacity: 0.3,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 5 },
    elevation: 5,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 24,
    textAlign: 'center',
    color: '#FFF', // Texto branco
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    borderColor: '#00FF9D', // Borda verde neon
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 10,
    backgroundColor: '#203a43', // Fundo escuro para os campos
  },
  input: {
    flex: 1,
    height: 50,
    color: '#FFF', // Texto branco
    fontSize: 16,
    paddingLeft: 10,
    backgroundColor: '#2c5364', // Fundo consistente
  },
  registerButton: {
    backgroundColor: '#00FF9D', // Botão verde neon
    padding: 15,
    borderRadius: 10,
    alignItems: 'center',
    marginTop: 20,
    borderWidth: 1,
    borderColor: '#FFF', // Borda branca
  },
  registerButtonText: {
    color: '#203a43', // Texto escuro para contraste
    fontSize: 16,
    fontWeight: 'bold',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)', // Fundo escuro translúcido
  },
  modalContent: {
    backgroundColor: '#203a43', // Fundo escuro
    padding: 20,
    borderRadius: 10,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#00FF9D', // Borda neon
    shadowColor: '#000',
    shadowOpacity: 0.5,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 5 },
    elevation: 5,
  },
  modalText: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 10,
    color: '#00FF9D', // Texto verde neon
  },
});

export default RegisterCompany;
